import { useCallback } from 'react';
import type { WidgetAction } from '@inference.engine/widget-html/web';
import { HtmlWidgetKey, type HtmlWidget } from '@inference.engine/widgets/web';
import { getWidgetStores } from '@/entities/widget';
import { getChatActions } from '@/lib/chat-actions/chat-actions-bindings';

export interface WidgetActionSource {
  /** The stored key, `html-widget:v1:<id>`. */
  key: string;
  agentName: string;
  artifactId?: string;
  widget: HtmlWidget;
}

/** What a widget's buttons do in the chat. `set-filter` never leaves the frame. */
export function useWidgetActions({ key, agentName, artifactId, widget }: WidgetActionSource) {
  const { chatStore, widgetStore, uiStore } = getWidgetStores();
  const { sendMessage } = chatStore.useChatActions();
  const agentId = chatStore.useChatStore((state) => state.currentAgentId as string | null);
  const { setValue } = chatStore.useChatStoreInput();
  const { setWidget } = widgetStore.useWidgetPanel();
  const { openSidePanel } = uiStore.useSidePanel();
  const busy = getChatActions().useIsChatBusy();

  return useCallback(
    (action: WidgetAction) => {
      if (action.type === 'send') {
        if (busy || !action.prompt || !agentId) return;
        void sendMessage({
          agentId,
          prompt: action.prompt,
          widgetAction: {
            artifactId: artifactId ?? '',
            widgetId: key.slice(HtmlWidgetKey.length + 1),
            ...(widget.title ? { title: widget.title } : {}),
            name: action.name,
            ...(action.row ? { row: action.row } : {}),
            filters: action.filters,
          },
        });
      } else if (action.type === 'prefill') {
        if (action.prompt) setValue(action.prompt);
      } else if (action.type === 'open-panel') {
        setWidget({ key, agentName, artifactId, data: widget });
        openSidePanel('widget');
      }
    },
    [
      busy,
      agentId,
      sendMessage,
      setValue,
      setWidget,
      openSidePanel,
      key,
      agentName,
      artifactId,
      widget,
    ],
  );
}
